import { FC, useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Divider, Grid, Stack, Typography, useTheme } from '@mui/material';
import * as yup from 'yup';

import { FormRoot, RenderFormField, SideModal } from '@/components';
import { Button } from '@/components/TailwindComponents';
import {
  towingCompanies,
  TowRecordFormValues,
} from '@/pages/Tools/TowingManagement/data';

interface TowRecordFormPanelProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TowRecordFormValues) => void;
  defaultValues?: TowRecordFormValues;
  mode?: 'add' | 'edit';
}

const towRecordSchema = yup.object({
  dateTime: yup.string().required('Date and Time is required'),
  towType: yup.string().required('Tow Type is required'),
  officerName: yup.string().when('towType', {
    is: 'police-initiated',
    then: (schema) => schema.required('Officer Name is required'),
    otherwise: (schema) => schema.optional(),
  }),
  badgeNumber: yup.string().when('towType', {
    is: 'police-initiated',
    then: (schema) => schema.required('Badge Number is required'),
    otherwise: (schema) => schema.optional(),
  }),
  licensePlate: yup.string().required('License Plate is required'),
  vin: yup.string().required('VIN is required'),
  year: yup.string().required('Year is required'),
  make: yup.string().required('Make is required'),
  model: yup.string().required('Model is required'),
  color: yup.string().optional(),
  towFromLocation: yup.string().required('Tow From Location is required'),
  destination: yup.string().required('Destination is required'),
  towingCompany: yup.string().required('Towing Company is required'),
  status: yup.string().required('Status is required'),
  reason: yup.string().required('Reason is required'),
  estimatedCost: yup.string().optional(),
  actualCost: yup.string().required('Actual Cost is required'),
  additionalNotes: yup.string().optional(),
});

const EMPTY_FORM_VALUES: TowRecordFormValues = {
  dateTime: '',
  towType: '',
  officerName: '',
  badgeNumber: '',
  licensePlate: '',
  vin: '',
  year: '',
  make: '',
  model: '',
  color: '',
  towFromLocation: '',
  destination: '',
  towingCompany: '',
  status: '',
  reason: '',
  estimatedCost: '',
  actualCost: '',
  additionalNotes: '',
};

export const TowRecordFormPanel: FC<TowRecordFormPanelProps> = ({
  open,
  onClose,
  onSubmit,
  defaultValues,
  mode = 'add',
}) => {
  const theme = useTheme();

  const towingCompanyOptions = towingCompanies.map((company) => ({
    label: company,
    value: company,
  }));

  const methods = useForm<any>({
    resolver: yupResolver(towRecordSchema),
    defaultValues: EMPTY_FORM_VALUES,
    mode: 'all',
  });

  const { reset, handleSubmit, control } = methods;

  const towType = useWatch({
    control,
    name: 'towType',
  });

  useEffect(() => {
    reset(defaultValues ?? EMPTY_FORM_VALUES);
  }, [defaultValues, reset]);

  const handleFormSubmit = (data: TowRecordFormValues) => {
    onSubmit(data);
    reset(EMPTY_FORM_VALUES);
  };

  const handleClose = () => {
    reset(EMPTY_FORM_VALUES);
    onClose();
  };

  const random = String(Math.floor(Math.random() * 100) + 1).padStart(3, '0');

  return (
    <SideModal open={open} onClose={handleClose} width="700px">
      <Stack
        sx={{
          backgroundColor: theme.palette.background.default,
          height: '100%',
          p: 3,
          overflowY: 'auto',
        }}
      >
        <Typography variant="h2" fontWeight={600} fontSize="1.5rem" mb={2}>
          {mode === 'edit' ? 'Edit tow record' : 'Add tow record'}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <FormProvider {...methods}>
          <FormRoot onSubmit={handleSubmit(handleFormSubmit)} noValidate>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12}>
                <Typography gutterBottom>Incident number</Typography>
                <Typography variant="subtitle2">
                  TOW-{new Date().getFullYear()}-{random}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <RenderFormField
                  name="dateTime"
                  label="Date and Time"
                  type="datetime"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <RenderFormField
                  name="towType"
                  label="Tow Type"
                  type="select"
                  rules={{ required: true }}
                  items={[
                    { label: 'Police', value: 'police-initiated' },
                    { label: 'EMS', value: 'ems' },
                    { label: 'Fire', value: 'fire' },
                    { label: 'Private', value: 'private' },
                  ]}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom>
              Initiator Information
            </Typography>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={6}>
                <RenderFormField
                  name="officerName"
                  label={towType === 'private' ? 'Name' : 'Officer Name"'}
                  rules={{
                    required: towType === 'police-initiated' ? true : false,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <RenderFormField
                  name="badgeNumber"
                  label={
                    towType === 'private' ? 'Phone number' : 'Badge Number'
                  }
                  rules={{
                    required: towType === 'police-initiated' ? true : false,
                  }}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom>
              Vehicle Information
            </Typography>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12}>
                <RenderFormField
                  name="licensePlate"
                  label="License Plate"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <RenderFormField
                  name="vin"
                  label="VIN"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <RenderFormField
                  name="year"
                  label="Year"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid item xs={4}>
                <RenderFormField
                  name="make"
                  label="Make"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid item xs={4}>
                <RenderFormField
                  name="model"
                  label="Model"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid item xs={4}>
                <RenderFormField name="color" label="Color" />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom>
              Location and Towing Details
            </Typography>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12}>
                <RenderFormField
                  name="towFromLocation"
                  label="Tow From Location"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <RenderFormField
                  name="destination"
                  label="Destination"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <RenderFormField
                  name="towingCompany"
                  label="Towing Company"
                  type="select"
                  rules={{ required: true }}
                  items={towingCompanyOptions}
                />
              </Grid>
              <Grid item xs={6}>
                <RenderFormField
                  name="status"
                  label="Status"
                  type="select"
                  rules={{ required: true }}
                  items={[
                    {
                      label: 'Completed',
                      value: 'completed',
                    },
                    {
                      label: 'Not assigned',
                      value: 'notAssigned',
                    },
                    {
                      label: 'In progress',
                      value: 'inProgress',
                    },
                    {
                      label: 'Released',
                      value: 'released',
                    },
                  ]}
                />
              </Grid>
              <Grid item xs={12}>
                <RenderFormField
                  name="reason"
                  label="Reason to Tow"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <RenderFormField
                  name="estimatedCost"
                  label="Estimated Cost ($)"
                />
              </Grid>
              <Grid item xs={6}>
                <RenderFormField
                  name="actualCost"
                  label="Actual Cost ($)"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <RenderFormField
                  name="additionalNotes"
                  label="Additional Notes"
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>

            <Box width="100%">
              <Stack direction="row" justifyContent="flex-end" gap={2} mt={4}>
                <Button variant="outline" type="button" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit">
                  {mode === 'edit' ? 'Update' : 'Save'}
                </Button>
              </Stack>
            </Box>
          </FormRoot>
        </FormProvider>
      </Stack>
    </SideModal>
  );
};
